/**
 * BalanceHistoryTable Component
 * Table showing balance history for an account with edit and delete actions
 * WP-FLOW-12: Added edit functionality with supersession
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import Spinner from '@/components/ui/Spinner';
import BalanceEditModal from './BalanceEditModal';
import { accountsService } from '@/services/accounts';
import { ApiClientError } from '@/services/api';
import { accountKeys } from '@/hooks/useAccounts';
import { formatCurrency, formatDate } from '@/utils/format';
import { cn } from '@/utils/cn';
import type { BalanceEntry } from '@/types';

export interface BalanceHistoryTableProps {
  accountId: string;
  currency?: string;
  className?: string;
  pageSize?: number;
}

// Source display labels
const SOURCE_LABELS: Record<string, string> = {
  manual: 'Manual',
  statement: 'Statement',
  plaid: 'Plaid',
  akoya: 'Akoya',
};

export default function BalanceHistoryTable({
  accountId,
  currency = 'USD',
  className,
  pageSize = 10,
}: BalanceHistoryTableProps) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [editEntry, setEditEntry] = useState<BalanceEntry | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch balance history
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['balance-history', accountId, page, pageSize],
    queryFn: () =>
      accountsService.getBalanceHistoryPaginated(accountId, {
        limit: pageSize,
        offset: page * pageSize,
      }),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (entryId: string) =>
      accountsService.deleteBalanceEntry(accountId, entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['balance-history', accountId] });
      queryClient.invalidateQueries({ queryKey: accountKeys.all });
      setDeleteConfirm(null);
      setError(null);
    },
    onError: (err) => {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to delete balance entry. Please try again.');
      }
      setDeleteConfirm(null);
    },
  });

  const handleDelete = (entryId: string) => {
    deleteMutation.mutate(entryId);
  };

  const entries = data?.entries ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);
  const hasNextPage = page < totalPages - 1;
  const hasPrevPage = page > 0;

  if (isLoading) {
    return (
      <div className={cn('flex justify-center py-8', className)}>
        <Spinner size="lg" />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="error" className={className}>
        Failed to load balance history.{' '}
        <button
          type="button"
          className="underline"
          onClick={() => refetch()}
        >
          Try again
        </button>
      </Alert>
    );
  }

  if (entries.length === 0) {
    return (
      <div className={cn('text-center py-8 text-gray-500', className)}>
        <NoHistoryIcon className="mx-auto h-12 w-12 text-gray-400" />
        <p className="mt-2">No balance history available</p>
      </div>
    );
  }

  return (
    <div className={className}>
      {error && (
        <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Date
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Balance
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Source
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Notes
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {entries.map((entry: BalanceEntry) => (
              <tr key={entry.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  {formatDate(entry.effectiveDate)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium tabular-nums">
                  <span className={entry.balance < 0 ? 'text-red-600' : 'text-gray-900'}>
                    {formatCurrency(entry.balance, currency)}
                  </span>
                  {entry.availableBalance !== undefined && (
                    <span className="text-gray-500 text-xs block">
                      {formatCurrency(entry.availableBalance, currency)} available
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                    {SOURCE_LABELS[entry.source] || entry.source}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                  {entry.notes || '-'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                  {deleteConfirm === entry.id ? (
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(entry.id)}
                        isLoading={deleteMutation.isPending}
                      >
                        Confirm
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteConfirm(null)}
                        disabled={deleteMutation.isPending}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditEntry(entry)}
                        aria-label={`Edit balance entry from ${formatDate(entry.effectiveDate)}`}
                      >
                        <PencilIcon className="h-4 w-4 text-gray-400 hover:text-blue-500" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteConfirm(entry.id)}
                        aria-label={`Delete balance entry from ${formatDate(entry.effectiveDate)}`}
                      >
                        <TrashIcon className="h-4 w-4 text-gray-400 hover:text-red-500" />
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
          <div className="text-sm text-gray-700">
            Showing {page * pageSize + 1} to {Math.min((page + 1) * pageSize, total)} of {total} entries
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p - 1)}
              disabled={!hasPrevPage}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasNextPage}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      <BalanceEditModal
        isOpen={editEntry !== null}
        onClose={() => setEditEntry(null)}
        accountId={accountId}
        entry={editEntry}
        currency={currency}
      />
    </div>
  );
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
      />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
      />
    </svg>
  );
}

function NoHistoryIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

/**
 * Skeleton loading state for BalanceHistoryTable
 */
export function BalanceHistoryTableSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-10 bg-gray-100 rounded mb-2" />
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="h-12 bg-gray-50 rounded mb-1" />
      ))}
    </div>
  );
}
